import React, { useMemo } from 'react';

import { VirtualItem, Virtualizer } from '@tanstack/react-virtual';
import { ChevronDown, ChevronRight } from 'lucide-react';

import { useLanguage } from '../../../hooks';
import { FlattenedLocationItem, NestedLocation } from '../../../types/nestedLocations';
import { cn } from '../../../utils/utils';
import { Badge } from '../../shadcn-ui/badge';
import { Button } from '../../shadcn-ui/button';
import { Checkbox } from '../../shadcn-ui/checkbox';
import { LocationIcon } from '../LocationIcon';
import type { ToggleAllChildrenMode } from './LocationsSelectorReducer';

/**
 * Per-row state for the "toggle all children" shortcut (clickable link to select/deselect every
 * child of an expanded parent in group-selection mode).
 *
 * - `showAbove`: this row renders the shortcut above it (it is the first child of an engaged parent).
 * - `connectsWithShortcutBelow`: the next row renders a shortcut, so this row (its parent) drops
 *    its bottom border so the shortcut + first child read as a single block.
 * - `parentLocationId` / `mode`: target and action dispatched when the shortcut is clicked. Only
 *    set when `showAbove` is true.
 */
export interface ToggleAllChildrenShortcut {
  showAbove: boolean;
  connectsWithShortcutBelow: boolean;
  parentLocationId?: number;
  mode?: ToggleAllChildrenMode;
}

const EMPTY_SHORTCUT: ToggleAllChildrenShortcut = {
  showAbove: false,
  connectsWithShortcutBelow: false,
};

interface LocationSelectorRowProps {
  virtualItem: VirtualItem;
  item: FlattenedLocationItem;
  multipleSelection: boolean;
  nextItem: FlattenedLocationItem | null;
  virtualizer: Virtualizer<HTMLDivElement, Element>;
  onToggleExpand: (locationId: number) => void;
  onSelect: (location: NestedLocation) => void;
  toggleAllChildrenShortcut?: ToggleAllChildrenShortcut;
  onToggleAllChildren?: (parentLocationId: number, mode: ToggleAllChildrenMode) => void;
}

export function LocationSelectorRow({
  virtualItem,
  multipleSelection,
  item,
  nextItem,
  virtualizer,
  onToggleExpand,
  onSelect,
  toggleAllChildrenShortcut = EMPTY_SHORTCUT,
  onToggleAllChildren,
}: LocationSelectorRowProps) {
  const lang = useLanguage();

  const {
    location,
    level,
    isSelected,
    isSelectionIndeterminate,
    isExpanded,
    isHighlighted,
    childrenCount,
    selectedChildrenCount,
    isRootLocation,
  } = item;

  // By default, location should be selectable
  const isSelectable = location.selectable ?? true;

  const handleSelectionClick = (e: React.MouseEvent) => {
    e.stopPropagation();

    // If the location is not selectable and has children, toggle the expand state instead of doing nothing
    if (!isSelectable) {
      if (childrenCount > 0) {
        onToggleExpand(location.id);
      }
      return;
    }

    onSelect(location);
  };

  const handleExpandClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleExpand(location.id);
  };

  const renderLocationsCount = () => {
    const rootLocationsCount =
      childrenCount === 1 ? lang.locations.row.location : lang.locations.row.locations;
    const sublocationsCount =
      childrenCount === 1 ? lang.locations.row.sublocation : lang.locations.row.sublocations;
    const locationsCount = `${childrenCount} ${
      isRootLocation ? rootLocationsCount : sublocationsCount
    }`;
    return (
      <>
        {childrenCount > 0 && (
          <Badge
            variant={isRootLocation ? 'default' : 'secondary'}
            className='shrink-0'
          >
            {locationsCount}
          </Badge>
        )}
        {selectedChildrenCount > 0 && (
          <Badge
            variant='tertiary'
            className='shrink-0'
          >
            {selectedChildrenCount}{' '}
            {selectedChildrenCount === 1
              ? lang.locations.row.selected
              : lang.locations.row.selected_plural}
          </Badge>
        )}
      </>
    );
  };

  const paddingLeft = useMemo(() => {
    if (isRootLocation) return 12;
    if (childrenCount > 0 && !isRootLocation) {
      return (level - 1) * 20 + 12;
    }
    return (level - 1) * 20 + 24 + 12;
  }, [childrenCount, isRootLocation, level]);

  const { showAbove: showShortcutAbove, connectsWithShortcutBelow } = toggleAllChildrenShortcut;

  const cardClassName = cn(
    'border-l-[1px] border-r-[1px] border-gray-200 overflow-hidden',
    isRootLocation &&
      `border-t-[1px] ${isExpanded && childrenCount > 0 ? 'border-b-0' : 'border-b-[1px]'} ${
        isExpanded ? 'rounded-t-lg' : 'rounded-lg'
      }`,
    !isRootLocation &&
      showShortcutAbove &&
      'border-t-[1px] border-gray-200 rounded-t-none bg-white',
    !isRootLocation && connectsWithShortcutBelow && 'border-b-0',
    nextItem?.isRootLocation || !nextItem ? 'rounded-b-lg border-b-[1px]' : '',
  );

  const canRenderShortcutAbove =
    showShortcutAbove &&
    toggleAllChildrenShortcut.parentLocationId != null &&
    toggleAllChildrenShortcut.mode != null &&
    onToggleAllChildren != null;

  return (
    <div
      key={String(virtualItem.key)}
      data-index={virtualItem.index}
      ref={virtualizer.measureElement}
      style={{ transform: `translateY(${virtualItem.start}px)` }}
      className={cn('absolute top-0 left-0 w-full', nextItem?.isRootLocation ? 'pb-2' : '')}
    >
      <div className={cardClassName}>
        {canRenderShortcutAbove && (
          <button
            type='button'
            onClick={(e) => {
              e.stopPropagation();
              onToggleAllChildren!(
                toggleAllChildrenShortcut.parentLocationId!,
                toggleAllChildrenShortcut.mode!,
              );
            }}
            style={{ paddingLeft: `${paddingLeft}px`, paddingRight: '16px' }}
            className='w-full text-left py-2.5 text-sm text-[#3E8FFA] font-medium'
          >
            {toggleAllChildrenShortcut.mode === 'deselectAll'
              ? lang.locations.row.deselectAll
              : lang.locations.row.selectAll}
          </button>
        )}
        <div
          className={cn(
            'flex items-center p-4 cursor-pointer',
            isHighlighted ? 'bg-blue-50 hover:bg-blue-100' : 'border-none hover:bg-gray-50',
            isSelected && !multipleSelection && 'bg-blue-100 hover:bg-blue-50',
          )}
          style={{ paddingLeft: `${paddingLeft}px` }}
          onClick={handleSelectionClick}
        >
          {childrenCount > 0 && !isRootLocation && (
            <Button
              variant='ghost'
              size='icon'
              className='h-5 w-5 p-0 shrink-0 mr-1'
              onClick={handleExpandClick}
            >
              <ChevronRight
                className={`h-4 w-4 transition-transform duration-200 ${
                  isExpanded ? 'rotate-90' : ''
                }`}
              />
            </Button>
          )}

          <div
            className={cn('shrink-0', !isSelectable ? 'cursor-not-allowed' : 'cursor-pointer')}
            hidden={!multipleSelection}
          >
            <Checkbox
              checked={isSelectionIndeterminate ? 'indeterminate' : isSelected}
              disabled={!isSelectable}
              className={cn(!isSelectable && 'opacity-50 cursor-not-allowed')}
            />
          </div>

          <div className='shrink-0 ml-2'>
            <LocationIcon placeType={location.place_type_id} />
          </div>

          <div className='ml-2 flex-1 min-w-0'>
            <span
              className={cn(
                'text-sm line-clamp-2 break-words',
                !isSelectable && 'text-gray-500 opacity-75',
              )}
            >
              {location.name}
            </span>
          </div>
          {childrenCount > 0 && isRootLocation && (
            <>
              <div className='ml-2 mr-1 gap-2 flex items-center'>{renderLocationsCount()}</div>
              <Button
                variant='ghost'
                size='icon'
                className='ml-auto cursor-pointer h-6 w-6 p-0'
                onClick={handleExpandClick}
              >
                <ChevronDown
                  className={`h-4 w-4 cursor-pointer transition-transform duration-200 ${
                    isExpanded ? 'rotate-180' : ''
                  }`}
                />
              </Button>
            </>
          )}

          {!isRootLocation && (
            <div className='ml-auto gap-1 flex items-center shrink-0'>{renderLocationsCount()}</div>
          )}
        </div>
      </div>
    </div>
  );
}
